import queryString from 'query-string'

/**
 * Tham số mang token đăng nhập trên URL. 3D trả token về bằng `#hash` (FAC-25086), nhưng đường
 * `?token=`/`?refresh=` cũ vẫn còn được `WithAuthentication` đọc nên vẫn phải phòng.
 *
 * Khai ở một chỗ để `useUrlFilters` (dọn thanh địa chỉ) và `loginUrl` (dọn referrer gửi sang 3D)
 * dùng CHUNG một danh sách — trước đây chỉ `useUrlFilters` lọc, `loginUrl` thì không, nên token vẫn
 * rò ra ngoài qua đúng cái URL mà ta vừa dọn.
 */
export const AUTH_PARAMS = ['token', 'refresh']

/**
 * Bỏ một số khoá khỏi query string, giữ nguyên phần còn lại.
 *
 * Trả về nguyên `search` nếu không có gì để bỏ: `queryString.stringify` sắp lại thứ tự khoá, mà
 * `useUrlFilterSync` so sánh chuỗi để quyết định có ghi URL hay không — đổi thứ tự vô cớ là sinh
 * một lượt ghi thừa.
 *
 * LUÔN bỏ kèm `AUTH_PARAMS` — xem ghi chú ở `setUrlParams`.
 */
export const omitUrlParams = (search, keys) => {
    if (!search) return ''

    const query = queryString.parse(search)
    const targets = keys.concat(AUTH_PARAMS.filter((key) => keys.indexOf(key) < 0))
    const present = targets.filter((key) => query[key] !== undefined)

    if (!present.length) return search

    present.forEach((key) => delete query[key])

    const next = queryString.stringify(query)

    return next ? `?${next}` : ''
}

/** Bỏ token khỏi query string. */
export const stripAuthParams = (search) => omitUrlParams(search, AUTH_PARAMS)

/**
 * Ghi đè một số khoá vào query string; giá trị rỗng/null nghĩa là XOÁ khoá đó.
 *
 * ⚠️ LUÔN bỏ `token`/`refresh` khỏi kết quả. `WithAuthentication` dọn thanh địa chỉ bằng
 *    `window.history.replaceState`, mà lệnh đó react-router KHÔNG biết — nên `location.search` trong
 *    bộ nhớ router vẫn còn token, và mọi `history.replace` sau đó sẽ bơm nó ngược lên thanh địa chỉ.
 *    `buildUrlSearch` của `useUrlFilters` đã lọc từ lâu vì đúng lý do này; hai hàm ở đây là đường ghi
 *    URL DÙNG CHUNG nên phải lọc y hệt, nếu không màn nào bỏ hook là mất lớp bảo vệ (đã xảy ra).
 *
 *    `output/OutputReportPage.js` từng dựng search bằng `queryString.stringify({...currentQuery,
 *    ...})` — spread bê nguyên mọi khoá lạ, kể cả token — đã gộp về `setUrlParams` sau vòng review
 *    14. Thêm đường ghi URL mới thì gộp về đây, đừng spread `location.search` đã parse.
 *
 *    ⚠️ Lệnh dưới liệt kê các chỗ còn gọi `queryString.stringify` **trong ba thư mục của nhóm màn
 *       này** (không phải toàn repo — `views/CheckPage` cũng tự dựng query, nằm ngoài phạm vi).
 *       Nó trả ĐÚNG hai dòng, và cả hai đều có chủ ý:
 *         - `views/helpers/useUrlFilters.js` — helper chính tắc, chính nó lọc AUTH_PARAMS;
 *         - `output/OutputDetailPage.js` — CHƯA gộp, dựng lại query của chính trang đang đứng;
 *           whitelist 4 khoá nên không rò token, nhưng xoá sạch mọi khoá khác.
 *       Thêm dòng thứ ba nghĩa là có đường ghi URL mới chưa gộp về đây.
 *
 *         grep -rn 'queryString.stringify' src/views/ProductionPlanning/ src/views/helpers/ src/share/ \
 *           | grep -v 'urlParams.js' | grep -v ':[0-9]*: *\*'
 *
 *       (`history.push(ROUTES.X + '?…')` sang route KHÁC thì không tính — sang trang khác mà không
 *       mang query trang cũ là đúng.)
 *
 * ⚠️ Giới hạn đã biết: hàm luôn parse rồi stringify lại, nên (a) thứ tự khoá bị sắp theo A-Z, và
 *    (b) chuỗi percent HỎNG sẵn trên URL bị mã hoá thêm một lần (`?q=%E0%A4%A` → `%25E0%25A4%25A`)
 *    vì `query-string` không decode được nó và trả lại nguyên văn dấu `%`. CÓ cách sửa sạch — chỉ
 *    splice đúng cặp khoá cần đổi và giữ nguyên văn bản thô của phần còn lại, khi đó không cặp nào
 *    bị decode nên không có gì để đoán — nhưng chưa đáng công: 7 call-site, và chuỗi percent hỏng
 *    chỉ đến từ URL gõ tay, không code nào trong repo sinh ra. (Giới hạn (a) và (b) áp cho CẢ
 *    `omitUrlParams` khi nó thực sự phải ghi lại.)
 *
 * Dùng cho trạng thái mà URL là nguồn duy nhất (ví dụ modal đang mở của một màn): đọc thẳng từ
 * `location`, ghi thẳng bằng `history`, không giữ bản sao trong state. Giữ bản sao là sinh ra hai
 * nguồn sự thật, và Back/Forward sẽ đổi URL mà không đổi state — màn hình đứng im.
 */
export const setUrlParams = (search, patch) => {
    const query = queryString.parse(search || '')

    Object.keys(patch).forEach((key) => {
        const value = patch[key]

        if (value === '' || value === null || value === undefined) {
            delete query[key]
        } else {
            query[key] = value
        }
    })

    // Sau vòng patch, không phải trước: đặt trước thì `setUrlParams(search, {token: 'X'})` vẫn ghi
    // được token ra URL — đúng cái thứ hàm này hứa chặn.
    AUTH_PARAMS.forEach((key) => delete query[key])

    const next = queryString.stringify(query)

    return next ? `?${next}` : ''
}
